import {
  ChildUser,
  Family,
  FamilyChildProfile,
  FamilyParentProfile,
  UserRole,
  QuestInstance,
  QuestSeries,
  JoonUIColor,
  CustomReward,
  LoggedInParent,
  QuestStatus,
  Gender,
  DoterSpecies,
} from "@joonapp/web-shared"
import dayjs, { Dayjs } from "dayjs"
import { RRule, Weekday } from "rrule"

import { ANALYTIC_EVENTS, trackAnalyticEvent } from "./analytics"
import { localStorageItems } from "./storage"
import {
  completeQuestStatuses,
  openQuestStatuses,
  parentAvatarImages,
  rejectedQuestStatuses,
  userIcons,
} from "../constants"
import i18n from "../locales/i18n"
import {
  CustomErrorInterface,
  TherapistProfileWithPatients,
  Subscriber,
  Invitation,
  InvitationGroup,
  ShopItem,
  ShopItemType,
  SubscriberAPIResponse,
  ParentDataInterface,
  QuestionInterface,
  ReferralGiftCard,
} from "../types"
import { QuestSectionType } from "../types/quests"
import {
  OnboardingCustomRewardTemplate,
  OnboardingCustomRewardTemplateWithUserIds,
} from "../types/rewards"

// Create an Error with custom message and code
export function CustomError(
  code: string,
  message: string
): CustomErrorInterface {
  return { ...new Error(message), code }
}

export function hasActivePlanSync(subscriber: Subscriber): boolean {
  const entitlements = subscriber.entitlements
  if (entitlements.premium) {
    const expire_date = entitlements.premium.expires_date
    const expire_date1 = new Date(expire_date)

    const today = new Date()

    return expire_date1 > today
  }
  // Q: Should return boolean false at end?
  return false
}

function getPlanType(subscriber: Subscriber) {
  if (subscriber.management_url) {
    if (subscriber.management_url.includes("apple")) {
      return "apple"
    } else {
      return "stripe"
    }
  } else {
    return "promotional"
  }
}

export function hasPurchasedPlanBefore(subscriber: Subscriber) {
  // Just check if there is anything for the premium entitlements
  return !!subscriber.entitlements.premium
}

export function getActivePlan(subscriber: Subscriber) {
  const entitlements = subscriber.entitlements
  if (entitlements.premium) {
    const expire_date = entitlements.premium.expires_date
    const expire_date1 = new Date(expire_date)

    const today = new Date()

    return {
      isActive: expire_date1 > today,
      planType: getPlanType(subscriber),
    }
  }

  // Q: Should have a return type in all cases. This ok?
  return { isActive: false, planType: "none" }
}

// Q: Doesn't look like this is being used anywhere?
export async function hasActivePlan(subscriberId: string) {
  const options = {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-Platform": "ios",
      "Content-Type": "application/json",
      // Authorization: `Bearer ${import.meta.env.VITE_REVCAT_KEY}`
      Authorization: "Bearer strp_bSiokSQhnXUFPUgkNSxpfctchCr",
    },
  }

  return fetch(
    `https://api.revenuecat.com/v1/subscribers/${subscriberId}`,
    options
  )
    .then((response) => response.json())
    .then((response) => {
      const subscriber = response.subscriber
      const entitlements = subscriber.entitlements

      if (entitlements.premium) {
        const expire_date = entitlements.premium.expires_date
        const expire_date1 = new Date(expire_date)

        const today = new Date()

        return expire_date1 > today
      }
    })
    .catch((err) => console.error(err))
}

export const isCoachQuest = (quest: QuestInstance | null | undefined) => {
  if (!quest) return false
  return quest.series.assigner_profile?.role === UserRole.COACH
}

export const sortByRoutine = (data: QuestInstance[]) => {
  const routineOrder = {
    morning: 0,
    afternoon: 1,
    night: 2,
    anytime: 3,
  } as { [key: string]: number }
  return data.sort((a, b) => {
    return routineOrder[a.series.routine] - routineOrder[b.series.routine]
  })
}

export const getDayAbbreviationsDDD = (repetitionArray: boolean[]) => {
  if (repetitionArray.every((val) => !val)) return []
  if (repetitionArray.filter((val) => !!val).length === 1)
    return [dayjs().day(repetitionArray.indexOf(true)).format("ddd")]
  const allDays = repetitionArray.map((day: boolean, i: number) =>
    day ? dayjs().day(i).format("ddd") : ""
  )
  const allDaysWithSundayLast = moveSundayToEnd(allDays)
  return allDaysWithSundayLast.filter((day) => !!day)
}

export const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1)

export const separateFamily = (user: any, family: Family | undefined) => {
  const separateFamily: {
    parent: FamilyParentProfile | null
    coparents: FamilyParentProfile[]
    children: FamilyChildProfile[]
  } = {
    parent: null,
    coparents: [],
    children: [],
  }

  if (user && family)
    family.profiles.forEach((profile) => {
      if (profile.user.id === user.id)
        separateFamily.parent = profile as FamilyParentProfile
      if (profile.role === UserRole.PARENT && profile.user.id !== user.id)
        separateFamily.coparents.push(profile)
      if (profile.role === UserRole.CHILD) separateFamily.children.push(profile)
    })

  return separateFamily
}

export const getChildrenFromFamily = (family: Family) =>
  family?.profiles.filter(
    (profile) => profile.role === UserRole.CHILD
  ) as FamilyChildProfile[]

export const noOnboardedChildren = (
  children: FamilyChildProfile[] | undefined
) => {
  return (
    !children ||
    children.length === 0 ||
    children.every((child) => !child.user.active_doter.date_hatched)
  )
}

export const getShopItemText = (shopItem: ShopItem) => {
  switch (shopItem.type) {
    case ShopItemType.ACCESSORY:
      return i18n.t("SHOP_ITEM_ACCESSORY")
    case ShopItemType.DECORATION:
      return i18n.t("SHOP_ITEM_DECORATION")
    case ShopItemType.FOOD:
      return i18n.t("SHOP_ITEM_FOOD")
    default:
      return ""
  }
}

export const getRelativeDay = (date: string) => {
  const day = dayjs(date)
  const today = dayjs()
  const diff = day.diff(today, "day")

  return diff === 1
    ? "Tomorrow"
    : diff === 0
      ? "Today"
      : diff === -1
        ? "Yesterday"
        : dayjs(date).format("MMM DD, YYYY")
}

export const getRepetitionText = (
  repetition: boolean[] | null | undefined,
  repeatsFrequency: number | null | undefined
) => {
  if (repetition?.every((val) => !!val)) {
    if (!repeatsFrequency || repeatsFrequency === 1) {
      return i18n.t("QUEST_REPEATS_DAILY")
    } else if (repeatsFrequency > 1) {
      return i18n.t("QUEST_REPEATS_DAILY_EVERY_N", { count: repeatsFrequency })
    }
  }
  let text = i18n.t("QUEST_REPEATS")

  if (repetition) {
    const days = getDayAbbreviationsDDD(repetition)
    if (Array.isArray(days)) {
      text += ` ${i18n.t("QUEST_REPEATS_ON", { days: days.join(", ") })}`
    } else if (days) {
      text += ` ${i18n.t("QUEST_REPEATS_ON", { days })}`
    } else {
      return i18n.t("QUEST_DOES_NOT_REPEAT")
    }
  }

  if (repeatsFrequency)
    text +=
      repeatsFrequency === 1
        ? ` ${i18n.t("QUEST_EVERY_WEEK")}`
        : ` ${i18n.t("QUEST_EVERY_N_WEEKS", { count: repeatsFrequency })}`

  return text
}

export const getChildImage = (child: ChildUser | null) => {
  return child?.active_doter?.type
    ? `/images/avatars/${child?.active_doter?.type}.png`
    : "/images/avatars/egg.png"
}

export const getUserFromFamily = (family: Family, user_id: number) => {
  return family?.profiles.find((profile) => profile?.user?.id === user_id)?.user
}

export const sortQuestSection = (questGroups: QuestInstance[][]) => {
  const getTimeValue = (timeString: string) => {
    const [time, period] = timeString.split(" ")
    const [hours, minutes] = time.split(":")
    let totalMinutes = parseInt(hours) * 60 + parseInt(minutes)
    if (period === "PM" && parseInt(hours) !== 12) {
      totalMinutes += 12 * 60
    }
    return totalMinutes
  }
  const getOrderValue = (order: number | null) => {
    return order === null ? Infinity : order
  }

  return questGroups.sort((a, b) => {
    const seriesA = a[0].series
    const seriesB = b[0].series

    if (seriesA.order !== null || seriesB.order !== null) {
      return getOrderValue(seriesA.order) - getOrderValue(seriesB.order)
    } else if (seriesA.mandatory !== seriesB.mandatory) {
      return seriesA.mandatory ? -1 : 1
    } else if (!!seriesA.reminder_time && !!seriesB.reminder_time) {
      const timeA = getTimeValue(seriesA.reminder_time)
      const timeB = getTimeValue(seriesB.reminder_time)
      return timeA - timeB
    } else if (!!seriesA.reminder_time && !seriesB.reminder_time) {
      return -1
    } else if (!seriesA.reminder_time && !!seriesB.reminder_time) {
      return 1
    } else return seriesA.date_created - seriesB.date_created
  })
}

export const sortQuestSeriesSection = (questGroups: QuestSeries[]) => {
  const getTimeValue = (timeString: string) => {
    const [time, period] = timeString.split(" ")
    const [hours, minutes] = time.split(":")
    let totalMinutes = parseInt(hours) * 60 + parseInt(minutes)
    if (period === "PM" && parseInt(hours) !== 12) {
      totalMinutes += 12 * 60
    }
    return totalMinutes
  }
  const getOrderValue = (order: number | null) => {
    return order === null ? Infinity : order
  }

  return questGroups.sort((seriesA, seriesB) => {
    if (seriesA.order !== null || seriesB.order !== null) {
      return getOrderValue(seriesA.order) - getOrderValue(seriesB.order)
    } else if (seriesA.mandatory !== seriesB.mandatory) {
      return seriesA.mandatory ? -1 : 1
    } else if (!!seriesA.reminder_time && !!seriesB.reminder_time) {
      const timeA = getTimeValue(seriesA.reminder_time)
      const timeB = getTimeValue(seriesB.reminder_time)
      return timeA - timeB
    } else if (!!seriesA.reminder_time && !seriesB.reminder_time) {
      return -1
    } else if (!seriesA.reminder_time && !!seriesB.reminder_time) {
      return 1
    } else return seriesA.date_created - seriesB.date_created
  })
}

export const getQuestCardBorderColor = (
  type: QuestSectionType,
  mandatory: boolean
) => {
  switch (type) {
    case QuestSectionType.COMPLETED:
      return JoonUIColor.semantic.success
    case QuestSectionType.REVIEW:
      return JoonUIColor.semantic.info
    case QuestSectionType.SKIPPED:
      return JoonUIColor.semantic.warning
    case QuestSectionType.REJECTED:
      return JoonUIColor.semantic.alert
    case QuestSectionType.ROUTINE:
      return mandatory ? JoonUIColor.border.accent : ""
  }
}

export const getQuestCardTabName = (type: QuestSectionType, index: number) => {
  if (index !== 0) return undefined
  if (type === QuestSectionType.ROUTINE) return undefined
  switch (type) {
    case QuestSectionType.COMPLETED:
      return i18n.t("QB_TAB_COMPLETED")
    case QuestSectionType.REVIEW:
      return i18n.t("QB_TAB_IN_REVIEW")
    case QuestSectionType.SKIPPED:
      return i18n.t("QB_TAB_SKIPPED")
    case QuestSectionType.REJECTED:
      return i18n.t("QB_TAB_REJECTED")
  }
}

export const getProfileDisplayName = (
  loggedInUser?: LoggedInParent,
  currentProfile?: FamilyChildProfile | FamilyParentProfile
) => {
  return (
    currentProfile?.user.name +
    (currentProfile?.user.id === loggedInUser?.id
      ? i18n.t("QUEST_ME_SUFFIX")
      : currentProfile?.nickname
        ? ` (${currentProfile?.nickname})`
        : "")
  )
}

// Creates a list of dates for the calendar
const getDates = () => {
  const dateList = []
  const numDays = 120 // ~2 months each direction. probably don't even need that much
  const startingDate = dayjs().subtract(numDays / 2, "day")
  for (let i = 0; i < numDays; i++) {
    dateList.push(startingDate.add(i, "day"))
  }
  return dateList
}
export const calendarDates = getDates()

// Takes in a list of children and their therapists, and returns
// a list of therapists with their patients
export const getTherapistsWithPatients = (
  response: { [childId: string]: any }[]
): TherapistProfileWithPatients[] => {
  const therapistsWithPatients = {} as {
    [childId: string]: TherapistProfileWithPatients
  }

  response.forEach((childTherapistMap) => {
    Object.entries(childTherapistMap).forEach(([childId, therapists]) => {
      therapists.forEach((therapist: any) => {
        const therapistId = therapist.user.id
        if (therapistsWithPatients[therapistId]) {
          therapistsWithPatients[therapistId].patients.push(Number(childId))
        } else {
          therapistsWithPatients[therapistId] = {
            ...therapist,
            patients: [childId],
          }
        }
      })
    })
  })

  return Object.values(therapistsWithPatients)
}

const therapistInstructionsSubject =
  "See my child's executive functioning improvements"
const therapistInstructionsBody = `A new reward system we've been using called Joon has improved my child's executive functioning more than visual charts. Joon is a video game my child is very motivated to use which holds them accountable without me having to nag, yell, or beg.

If you create a free therapist account on Joon, you'll be able to see their routine and track our routine progress outside of therapy, seeing which tasks they still struggle with daily.

To see my child's executive functioning progress, you can sign up at: https://joonapp.io/therapists-invite-2

Joon has helped over 500k families improve their executive functioning and is recommended by ADDitude Magazine and over 300 clinics in the US!
  `
export const emailInstructions = () => {
  const emailSubject = encodeURIComponent(therapistInstructionsSubject)
  const emailBody = encodeURIComponent(therapistInstructionsBody)
  const url = `mailto:?subject=${emailSubject}&body=${emailBody}`
  window.open(url)
  trackAnalyticEvent(ANALYTIC_EVENTS.SEND_THERAPIST_INVITE)
}
export const shareInstructions = () => {
  if (navigator.share) {
    navigator.share({
      title: therapistInstructionsSubject,
      text: therapistInstructionsBody,
    })
  }
  trackAnalyticEvent(ANALYTIC_EVENTS.SEND_THERAPIST_INVITE)
}

export const createErrorFromResponse = (err: any) => {
  let error = err.response.data
  if (typeof error == "string") return error
  if (Object.values(error).length > 0) {
    error = Object.values(error)[0]
    if (error.constructor === Array) return error[0]
    else return error
  }
}
export const createErrorFromSignupResponse = (err: any): string => {
  const error = err.response.data
  if (typeof error == "string") return error
  if (Object.entries(error).length > 0) {
    const errorKey = Object.entries(error)[0][0]
    const errorMessage = Object.entries(error)[0][1] as string | string[]
    if (errorMessage?.constructor === Array) {
      if (["email", "password"].includes(errorKey))
        return `${errorKey}: ${errorMessage[0]}`
      else return `${errorMessage[0]}`
    } else return errorMessage as string
  }
  return "Unknown error occurred. Please try again."
}

// These next two functions convert repetition array to rrule representation and back
export const convertToByWeekday = (booleanArray: boolean[]) => {
  if (!booleanArray) return []
  const weekdays: Weekday[] = [
    RRule.SU,
    RRule.MO,
    RRule.TU,
    RRule.WE,
    RRule.TH,
    RRule.FR,
    RRule.SA,
  ]

  return booleanArray
    .map((value, index) => (value ? weekdays[index] : null))
    .filter((day): day is Weekday => day !== null)
}

export const getRepetitionArrayFromRrule = (rruleString: string): boolean[] => {
  const rrule = RRule.fromString(rruleString)
  const weekdays = [6, 0, 1, 2, 3, 4, 5] // Mapping to RRule's internal representation

  return weekdays.map((day) => rrule.options.byweekday?.includes(day) || false)
}

export const getRepetitionIntervalFromRrule = (rruleString: string): number => {
  return RRule.fromString(rruleString).options.interval
}

export const isTestEnv =
  import.meta.env.MODE === "development" ||
  window.location.hostname.endsWith(".vercel.app")

export const groupInvites = (invites: Invitation[]): InvitationGroup[] => {
  const therapistInvites: { [key: string]: InvitationGroup } = {}
  for (const invite of invites) {
    if (!therapistInvites[invite.invitee_email])
      therapistInvites[invite.invitee_email] = []
    therapistInvites[invite.invitee_email].push(invite)
  }
  return Object.values(therapistInvites)
}

export const getParentAvatarImageUrls = (avatar: DoterSpecies) => {
  switch (avatar) {
    case DoterSpecies.DRAGON:
      return parentAvatarImages[DoterSpecies.DRAGON]
    case DoterSpecies.BEAR:
      return parentAvatarImages[DoterSpecies.BEAR]
    case DoterSpecies.FOX:
      return parentAvatarImages[DoterSpecies.FOX]
    default:
      return parentAvatarImages[DoterSpecies.FOX]
  }
}

export const isUsingInAppBrowser = (() => {
  // @ts-ignore
  var ua = navigator.userAgent || navigator.vendor || window.opera || ""
  return (
    /FBAN|FBAV|FBBV|FB|FBDV|FBMD|FBSV|FBSS|FBCR|FBID|FBLC|FBOP|FBCA/i.test(
      ua
    ) ||
    /Instagram/i.test(ua) ||
    /Tiktok|TT/i.test(ua)
  )
})()

export const getDayAbbreviation = (day: number) => {
  const dayAbbrevationMap = {
    0: "Su",
    1: "M",
    2: "T",
    3: "W",
    4: "Th",
    5: "F",
    6: "Sa",
  } as { [key: number]: string }
  return dayAbbrevationMap[day]
}

export const dateIsToday = (date: Dayjs) => {
  return dayjs().isSame(date, "day")
}

export const groupQuestsByTherapist = (
  parentTasks: QuestInstance[] | undefined
) => {
  if (!parentTasks) return []
  const therapists = {} as Record<number, QuestInstance[]>
  parentTasks.forEach((task) => {
    const therapist = task.series.assigner_profile?.user_id
    if (therapist && !therapists[therapist]) {
      therapists[therapist] = [task]
    } else if (therapist) {
      therapists[therapist].push(task)
    }
  })
  return Object.values(therapists)
}

export const groupQuestsByDate = (quests: QuestInstance[]) => {
  const groupedQuests: Record<string, QuestInstance[]> = {}
  quests.forEach((quest) => {
    const date = dayjs(quest.date).format("YYYY-MM-DD")
    if (!groupedQuests[date]) groupedQuests[date] = []
    groupedQuests[date].push(quest)
  })
  return Object.entries(groupedQuests).sort(
    ([dateA], [dateB]) => dayjs(dateB).unix() - dayjs(dateA).unix()
  )
}

export const getReadableFileSize = (size: number | undefined) => {
  if (!size) return ""
  var i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024))
  return (
    +(size / Math.pow(1024, i)).toFixed(2) * 1 +
    " " +
    ["B", "kB", "MB", "GB", "TB"][i]
  )
}

export const getCurrentSubscription = (
  subscriberData: SubscriberAPIResponse | undefined
) => {
  if (!subscriberData) return null
  return Object.values(subscriberData?.subscriber.subscriptions).sort(
    (a, b) => dayjs(b.expires_date).unix() - dayjs(a.expires_date).unix()
  )[0]
}

export const getSubscriptionType = (subscription: any) => {
  if (!subscription?.store) return "None"
  const cleanedStore = subscription.store
    .split("_")
    .map(capitalizeFirstLetter)
    .join(" ")
  const cleanedPeriodType = capitalizeFirstLetter(subscription.period_type)

  return cleanedStore + " - " + cleanedPeriodType
}

export const formatDate = (
  date: Date,
  format: string = "MMMM D, YYYY",
  relative = true
) => {
  const today = new Date()
  const yesterday = new Date(today)
  const tomorrow = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (relative) {
    if (date.toDateString() === today.toDateString()) {
      return "Today"
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday"
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow"
    }
  }

  return dayjs(date).format(format)
}

export const formattedCentsToDollars = (cents: number) =>
  Number((cents / 100).toFixed(2))

export const isValidPhoneNumber = (phoneNumber: string) => {
  // This regex requires a + followed by country code and 8-15 more digits
  const phoneNumberRegex =
    /^\+(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\d{8,15}$/
  return phoneNumberRegex.test(phoneNumber)
}

export const getUserReasons = (
  parentData: any,
  parentQuestionnaires: QuestionInterface[]
) => {
  const updatedParentData = {
    ...parentData,
    parentQuestionnaireMapping: JSON.parse(
      localStorage.getItem(localStorageItems.parentQuestionnaireAnswers) || "{}"
    ),
  } as ParentDataInterface
  if (
    !updatedParentData.parentQuestionnaireMapping ||
    Object.keys(updatedParentData.parentQuestionnaireMapping).length === 0
  ) {
    // placeholder reasons
    return [
      {
        icon: userIcons[2],
        text: "Help my child keep track of their tasks",
      },
      { icon: userIcons[6], text: "Organize a routine for my child" },
    ]
  }

  if (Object.keys(updatedParentData.parentQuestionnaireMapping).length > 0) {
    const firstAnswer = updatedParentData.parentQuestionnaireMapping?.["1"]?.[0]
    const secondAnswer =
      updatedParentData.parentQuestionnaireMapping?.["2"]?.[0]

    const firstAnswerText = parentQuestionnaires[0].answers.find(
      (answer) => answer.id === firstAnswer
    )?.answer
    const secondAnswerText = parentQuestionnaires[1].answers.find(
      (answer) => answer.id === secondAnswer
    )?.answer

    const icon1 = userIcons[firstAnswer]
    const icon2 = userIcons[secondAnswer]
    return [
      { id: firstAnswer, icon: icon1, text: firstAnswerText },
      { id: secondAnswer, icon: icon2, text: secondAnswerText },
    ]
  }
}

export const getScreenTimeConfigurations = (
  reward: CustomReward | null,
  userId?: number
) => {
  if (!reward) return { linkedDevices: [], numLinkedDevices: 0 }
  const linkedDevices = new Set()
  reward.assignments
    .filter((assignment) => !userId || assignment.user_id === userId)
    .forEach((assignment) => {
      assignment.screen_time_configurations.forEach((configuration) => {
        linkedDevices.add(configuration.id)
      })
    })
  return {
    numLinkedDevices: linkedDevices.size,
    linkedDevices: [...linkedDevices],
  }
}

const moveSundayToEnd = (recurrenceByDay: string[]) => {
  const sundayValues = ["su", "sun"]
  const sundayIndex = recurrenceByDay.findIndex((value) =>
    sundayValues.includes(value.toLowerCase())
  )
  if (sundayIndex === -1) return recurrenceByDay
  const sunday = recurrenceByDay.splice(sundayIndex, 1)
  recurrenceByDay.push(sunday[0])
  return recurrenceByDay
}

// given a recurrenceByDay array in format ['MO', 'TU', 'WE', 'TH', 'FR', ..]
// return a string value of "daily" or 'weekends' or "weekdays" or "Mo, Tu, We, Th, Fr, Sa, Su"
// and if time is provided, return the time in the format of "Mo, Tu, We, Th, Fr, Sa, Su at HH:MM"
export const getDayRepetitionString = (
  recurrenceByDay: string[] | null,
  time: string | null
) => {
  if (!recurrenceByDay) return ""
  const recurrenceByDayWithSundayLast = moveSundayToEnd(recurrenceByDay)
  let output = ""

  const isDaily = recurrenceByDay.length === 7
  const isWeekends =
    recurrenceByDay.length === 2 &&
    recurrenceByDay.includes("SA") &&
    recurrenceByDay.includes("SU")
  const isWeekdays =
    recurrenceByDay.length === 5 &&
    !recurrenceByDay.includes("SA") &&
    !recurrenceByDay.includes("SU")

  // Map rrule day codes to more readable format
  const dayCodeMap: { [key: string]: string } = {
    MO: i18n.t("DAY_ABBR_MON"),
    TU: i18n.t("DAY_ABBR_TUE"),
    WE: i18n.t("DAY_ABBR_WED"),
    TH: i18n.t("DAY_ABBR_THU"),
    FR: i18n.t("DAY_ABBR_FRI"),
    SA: i18n.t("DAY_ABBR_SAT"),
    SU: i18n.t("DAY_ABBR_SUN"),
  }

  if (isDaily) output = i18n.t("QUEST_DAILY")
  else if (isWeekends) output = i18n.t("QUEST_RECUR_WEEKENDS")
  else if (isWeekdays) output = i18n.t("QUEST_RECUR_WEEKDAYS")
  else
    output = recurrenceByDayWithSundayLast
      .map((day) => dayCodeMap[day] || day)
      .join(", ")

  if (time) {
    if (recurrenceByDay.length === 0) output = `${time}`
    else output = i18n.t("QUEST_DAYS_AT", { days: output, time })
  }

  return output
}

export const getDaysFromRRuleString = (rruleString: string): string[] => {
  const rrule = RRule.fromString(rruleString)

  if (!rrule.options.byweekday || !Array.isArray(rrule.options.byweekday)) {
    return []
  }

  // Map RRule's internal weekday objects to their string representations
  const weekdayMap: { [key: number]: string } = {
    0: "MO",
    1: "TU",
    2: "WE",
    3: "TH",
    4: "FR",
    5: "SA",
    6: "SU",
  }

  return moveSundayToEnd(rrule.options.byweekday.map((day) => weekdayMap[day]))
}

export const getMondayOfWeek = () => {
  const isSunday = dayjs().day() === 0
  return isSunday
    ? dayjs().subtract(6, "days")
    : dayjs().startOf("week").add(1, "day")
}

export const groupRewardTemplatesById = (
  templates: OnboardingCustomRewardTemplate[]
): OnboardingCustomRewardTemplateWithUserIds[] => {
  const groupedTemplatesMap = new Map<
    number,
    OnboardingCustomRewardTemplateWithUserIds
  >()
  templates.forEach((template) => {
    if (groupedTemplatesMap.has(template.id)) {
      groupedTemplatesMap.get(template.id)!.user_ids.push(template.user_id)
    } else {
      groupedTemplatesMap.set(template.id, {
        ...template,
        user_ids: [template.user_id],
      })
    }
  })
  return Array.from(groupedTemplatesMap.values())
}

export const checkStatusEquality = (
  status1: QuestStatus,
  status2: QuestStatus
) => {
  switch (status1) {
    case QuestStatus.COMPLETED:
    case QuestStatus.VERIFIED:
    case QuestStatus.REDEEMED:
      return completeQuestStatuses.includes(status2)
    case QuestStatus.REJECTED:
    case QuestStatus.REJECTED_VIEWED:
      return rejectedQuestStatuses.includes(status2)
    case QuestStatus.OPEN:
    case QuestStatus.RETRY:
      return openQuestStatuses.includes(status2)
    case QuestStatus.SKIPPED:
      return status2 === QuestStatus.SKIPPED
    default:
      return false
  }
}

export const getErrorMessageFromStatuses = ({
  initialStatus,
  newStatus,
  isParentQuest,
}: {
  initialStatus: QuestStatus
  newStatus: QuestStatus
  isParentQuest: boolean
}) => {
  if (checkStatusEquality(initialStatus, newStatus))
    return i18n.t("QUEST_TOAST_ERR_UPDATE_NOTE")

  switch (newStatus) {
    case QuestStatus.COMPLETED:
    case QuestStatus.VERIFIED:
    case QuestStatus.REDEEMED:
      return isParentQuest
        ? i18n.t("QUEST_TOAST_ERR_COMPLETING")
        : i18n.t("QUEST_TOAST_ERR_VERIFYING")
    case QuestStatus.SKIPPED:
      return i18n.t("QUEST_TOAST_ERR_SKIPPING")
    case QuestStatus.REJECTED:
    case QuestStatus.REJECTED_VIEWED:
      return i18n.t("QUEST_TOAST_ERR_REJECTING")
    case QuestStatus.OPEN:
    case QuestStatus.RETRY:
      switch (initialStatus) {
        case QuestStatus.REDEEMED:
          return i18n.t("QUEST_TOAST_ERR_CANT_UNDO_REDEEMED")
        case QuestStatus.COMPLETED:
        case QuestStatus.VERIFIED:
          return isParentQuest
            ? i18n.t("QUEST_TOAST_ERR_UNDO_COMPLETION")
            : i18n.t("QUEST_TOAST_ERR_UNDO_VERIFICATION")
        case QuestStatus.SKIPPED:
          return i18n.t("QUEST_TOAST_ERR_UNDO_SKIP")
        case QuestStatus.REJECTED:
        case QuestStatus.REJECTED_VIEWED:
          return i18n.t("QUEST_TOAST_ERR_UNDO_REJECTION")
        default:
          return i18n.t("QUEST_TOAST_ERR_UNDO_ACTION")
      }
    default:
      return i18n.t("QUEST_TOAST_ERR_UPDATING")
  }
}

export const getSuccessMessageFromStatuses = ({
  initialStatus,
  newStatus,
  isParentQuest,
}: {
  initialStatus: QuestStatus
  newStatus: QuestStatus
  isParentQuest: boolean
}) => {
  if (checkStatusEquality(initialStatus, newStatus))
    return i18n.t("QUEST_TOAST_NOTE_SAVED")

  switch (newStatus) {
    case QuestStatus.COMPLETED:
    case QuestStatus.VERIFIED:
    case QuestStatus.REDEEMED:
      return isParentQuest
        ? i18n.t("QUEST_TOAST_COMPLETED")
        : i18n.t("QUEST_TOAST_VERIFIED")
    case QuestStatus.SKIPPED:
      return i18n.t("QUEST_TOAST_SKIPPED")
    case QuestStatus.REJECTED:
    case QuestStatus.REJECTED_VIEWED:
      return i18n.t("QUEST_TOAST_REJECTED")
    case QuestStatus.OPEN:
    case QuestStatus.RETRY:
      switch (initialStatus) {
        case QuestStatus.COMPLETED:
        case QuestStatus.VERIFIED:
        case QuestStatus.REDEEMED:
          return isParentQuest
            ? i18n.t("QUEST_TOAST_COMPLETION_UNDONE")
            : i18n.t("QUEST_TOAST_VERIFICATION_UNDONE")
        case QuestStatus.SKIPPED:
          return i18n.t("QUEST_TOAST_SKIP_UNDONE")
        case QuestStatus.REJECTED:
        case QuestStatus.REJECTED_VIEWED:
          return i18n.t("QUEST_TOAST_REJECTION_UNDONE")
        default:
          return i18n.t("QUEST_TOAST_ACTION_UNDONE")
      }
    default:
      return i18n.t("QUEST_TOAST_UPDATED")
  }
}

/**
 * Validates a date string in MM/DD/YYYY format
 * @param dateString - The date string to validate in MM/DD/YYYY format
 * @returns boolean indicating if the date is valid
 */
export const isValidDate = (dateString: string): boolean => {
  // First check if the string matches the expected format
  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
  if (!dateRegex.test(dateString)) {
    return false
  }

  // Use dayjs to validate the date
  const date = dayjs(dateString, "MM/DD/YYYY", true)
  return date.isValid()
}

export const getGenderLabel = (gender: Gender) => {
  switch (gender) {
    case Gender.MALE:
      return i18n.t("GENDER_BOY")
    case Gender.FEMALE:
      return i18n.t("GENDER_GIRL")
    case Gender.OTHER:
    case null:
      return i18n.t("GENDER_PREFER_NOT")
  }
}

export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = reject
    img.src = src
  })
}

export const formatSecondsToTimer = (secs: number) => {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}:${s.toString().padStart(2, "0")}`
}

export const convertTo12HourTime = (timeString: string): string => {
  const [hours, minutes] = timeString.split(":")
  const hour = parseInt(hours, 10)
  const minute = parseInt(minutes, 10)
  const period = hour >= 12 ? "PM" : "AM"
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`
}

export const convertTo24HourTime = (timeString: string): string => {
  const [hours, minutes] = timeString.split(":")
  const hour = parseInt(hours, 10)
  const minute = parseInt(minutes, 10)
  const period = timeString.includes("PM") ? 12 : 0
  return `${(hour + period).toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
}

export const shareGuestPass = async (
  referralGiftCard: ReferralGiftCard,
  onOpenThoughtfulModal: () => void
) => {
  const { uuid, url } = referralGiftCard
  const emailSubject = i18n.t("REFERRAL_SHARE_SUBJECT")
  const instructions = i18n.t("REFERRAL_SHARE_BODY")
  const emailBody = `${instructions}\n\n${url}`
  trackAnalyticEvent(ANALYTIC_EVENTS.SHARE_REFERRAL_GIFT_CARD, {
    uuid,
  })

  try {
    // If the web share API is supported by the browser
    if (navigator.share) {
      await navigator.share({
        title: instructions,
        url,
      })
      onOpenThoughtfulModal()
    } else {
      // Else open email to share
      const subject = encodeURIComponent(emailSubject)
      const body = encodeURIComponent(emailBody)

      const url = `mailto:?subject=${subject}&body=${body}`
      window.open(url)
    }
  } catch (error) {
    console.error("Error sharing:", error)
  }
}
